import Button from '@/components/Buttons/Button';
import { SubmitFooterType } from '@/components/Voice/VoiceModal/SelectVoiceContent/SelectVoiceContent';
import { VoiceModal } from '@/components/Voice/VoiceModal/VoiceModal';
import { isVoiceSupportingLanguage, VoiceFiltersEnum } from '@/hooks/useFilteredVoices';
import { getDefautlVoiceForExpressiveAvatar } from '@/pages/AdvancedEditor/utils/expressive-utils';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useAgentEditorStore } from '@/state/root';
import { IVoice, PresenterUsecase, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

const Footer = styled.div`
    display: flex;
    padding: 16px 36px;
    justify-content: end;
    gap: 16px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
`;

interface Props {
    open: boolean;
    onClose: () => void;
}

export const AgentVoiceModal = observer(({ open, onClose }: Props) => {
    const agentEditorStore = useAgentEditorStore();
    const [selectedVoiceInternalState, setSelectedVoiceInternalState] = useState<IVoice | null>(agentEditorStore.ttsSelectedVoice);

    const { allAvatars } = useAllAvatars(PresenterUsecase.AgentV2);
    const presenter = allAvatars.find(p => p.id === agentEditorStore.selectedPresenter);
    const defaultVoice = getDefautlVoiceForExpressiveAvatar(presenter);
    const injectedVoices = defaultVoice ? [defaultVoice] : [];

    const voice = selectedVoiceInternalState;

    function handleSelectedVoiceChange(voice: IVoice) {
        setSelectedVoiceInternalState(voice);
    }

    const handleApply = (selectedVoice: IVoice | null) => {
        if (!selectedVoice) {
            return;
        }

        runInAction(() => {
            agentEditorStore.setTtsSelectedVoice(selectedVoice, true);
            agentEditorStore.setTtsVoiceConfig((prev: any) => ({ ...prev, style: '' }));
            agentEditorStore.setErrors(errors => ({ ...errors, ttsError: null }));

            if (!agentEditorStore.ttsSelectedLanguage) {
                return;
            }

            if (isVoiceSupportingLanguage(selectedVoice, agentEditorStore.ttsSelectedLanguage)) {
                return;
            }

            agentEditorStore.setTtsSelectedLanguage(null);
        });
    };

    const SubmitFooter: SubmitFooterType = ({ onSubmitted, selectedVoice }: { onSubmitted: () => void; selectedVoice: IVoice | null }) => (
        <Footer>
            <Button
                data-testid="video-editor-voice-modal-apply-this-avatar-voice-button"
                onClick={() => {
                    handleApply(selectedVoice);
                    onSubmitted();
                }}
                variant="contained"
                color="secondary">
                Apply voice
            </Button>
        </Footer>
    );

    const voicesCustomFilter = (voice: IVoice) => {
        return !(voice.name.includes(' HD ') || voice.name.endsWith(' HD'));
    };

    return (
        <VoiceModal
            open={open}
            onClose={onClose}
            SubmitFooter={SubmitFooter}
            selectedVoice={voice}
            onSelectedVoiceChange={handleSelectedVoiceChange}
            filters={{
                [VoiceFiltersEnum.LANGUAGE]: agentEditorStore.ttsSelectedLanguage || undefined,
                [VoiceFiltersEnum.PROVIDERS]: [Providers.Elevenlabs, Providers.Microsoft, Providers.AzureOpenAi],
                [VoiceFiltersEnum.CUSTOM]: voicesCustomFilter,
            }}
            hiddenFilters={[VoiceFiltersEnum.LANGUAGE, VoiceFiltersEnum.USE_CASE, VoiceFiltersEnum.VOICE_FUNCTION]}
            injectedVoices={injectedVoices}
            showLowLatencyTag
        />
    );
});
